import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from '../utils/authConfig.js';
import { ssoConfig } from './msalConfig.js';
import Logger from '../utils/logger.js';

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise once on app load
let redirectHandled = false;
let initialized = false;

const initializeMsal = async () => {
  if (redirectHandled) {
    return msalInstance;
  }

  try {
    // Initialize MSAL instance first (required for MSAL v3)
    if (!initialized) {
      await msalInstance.initialize();
      initialized = true;
      Logger.info('[MSAL] Instance initialized');

      // Log SSO capability status
      if (ssoConfig.isSSOCapable()) {
        Logger.info('[MSAL] SSO is available for automatic sign-in');
      }
    }

    // Handle redirect promise to complete the authentication flow
    const response = await msalInstance.handleRedirectPromise();
    redirectHandled = true;

    if (response) {
      Logger.info('[MSAL] Redirect authentication completed successfully');
      // Set the active account if not already set
      if (!msalInstance.getActiveAccount() && response.account) {
        msalInstance.setActiveAccount(response.account);

        // Record successful login for SSO detection
        const email = response.account.username || '';
        const domain = email.split('@')[1];
        if (domain) {
          ssoConfig.recordSuccessfulSSO(domain);
        }
      }
    }
  } catch (error) {
    Logger.error('[MSAL] Error handling redirect promise:', error);
    redirectHandled = true;
  }

  // Configure event callbacks
  msalInstance.addEventCallback(event => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      // Set active account after successful login
      msalInstance.setActiveAccount(event.payload.account);
      Logger.info('[MSAL] Login successful, active account set');

      // Record successful login for SSO detection
      const email = event.payload.account.username || '';
      const domain = email.split('@')[1];
      if (domain) {
        ssoConfig.recordSuccessfulSSO(domain);
      }
    }

    if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
      Logger.warn('[MSAL] Token acquisition failed:', event.error?.errorCode || event.error?.message);
    }

    if (event.eventType === EventType.SSO_SILENT_SUCCESS) {
      Logger.info('[MSAL] SSO silent login successful');

      // Record successful SSO
      if (event.payload?.account) {
        const email = event.payload.account.username || '';
        const domain = email.split('@')[1];
        if (domain) {
          ssoConfig.recordSuccessfulSSO(domain);
        }
      }
    }

    if (event.eventType === EventType.SSO_SILENT_FAILURE) {
      // This is expected for users who aren't signed into Azure AD
      Logger.debug('[MSAL] SSO silent login failed (user may need to sign in manually)');
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      Logger.info('[MSAL] Logout successful');
      // Clear SSO cache on logout
      ssoConfig.clearDomainCache();
    }
  });

  // Set active account if one exists but isn't set
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
    Logger.info('[MSAL] Active account set from existing accounts');
  }

  return msalInstance;
};

export { msalInstance, initializeMsal };
